import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Trophy, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { Navigation } from "@/components/Navigation";
import { getUserData, themesAPI, ThemeLeaderboardEntry, ThemeLibraryItem } from "@/lib/api";
import { translateDomainName, translateStrengthName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

/** Admin-only: full page showing theme info + every user ranked for that theme as cards. */
export default function AdminThemeLeaderboardPage() {
  const { themeId } = useParams();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(() => getUserData());
  const [theme, setTheme] = useState<ThemeLibraryItem | null>(null);
  const [entries, setEntries] = useState<ThemeLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!themeId) {
      navigate("/admin?tab=leaderboard");
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const [themesResponse, leaderboardResponse] = await Promise.all([
          themesAPI.getThemes(),
          themesAPI.getThemeLeaderboard(themeId),
        ]);

        const foundTheme = (themesResponse.data || []).find((item) => item.id === themeId);
        if (!foundTheme) {
          navigate("/admin?tab=leaderboard");
          return;
        }

        setTheme(foundTheme);
        setEntries(leaderboardResponse.data?.leaderboard || []);
      } catch (error: any) {
        toast({
          title: t("strengthsExplorer.leaderboardLoadFailed"),
          description: getLocalizedErrorMessage(error, "strengthsExplorer.leaderboardLoadFailedDesc"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [navigate, t, themeId, toast]);

  const color = theme ? STRENGTHS_EXPLORER_DOMAIN_COLORS[theme.domain] : undefined;

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => a.rank - b.rank),
    [entries]
  );

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />

      <main className="pt-24 px-4 sm:px-6 pb-12">
        <div className="container mx-auto max-w-6xl min-w-0 space-y-6">
          <Button variant="ghost" className="gap-2 px-0" asChild>
            <Link to="/admin?tab=leaderboard" dir={isRtl ? "rtl" : "ltr"}>
              <ArrowLeft className={`h-4 w-4 ${isRtl ? "rotate-180" : ""}`} />
              {t("strengthsExplorer.backToLeaderboard")}
            </Link>
          </Button>

          {isLoading || !theme ? (
            <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              {t("strengthsExplorer.loadingLeaderboard")}
            </Card>
          ) : (
            <>
              <Card className="border-s-4" style={{ borderInlineStartColor: color, borderInlineStartWidth: 4 }}>
                <CardHeader>
                  <div className="flex flex-wrap items-center gap-3">
                    <CardTitle className="text-2xl sm:text-3xl">
                      {translateStrengthName(theme.name, t, i18n.language)}
                    </CardTitle>
                    <Badge
                      className="border"
                      style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                    >
                      {translateDomainName(theme.domain, t, i18n.language)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {isRtl ? (theme.descriptionAr || theme.description) : theme.description}
                  </p>
                </CardContent>
              </Card>

              <div className="space-y-3">
                <h2 className="text-xl font-bold">{t("strengthsExplorer.leaderboardTitle")}</h2>

                {sortedEntries.length === 0 ? (
                  <Card className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground">
                    <Users className="h-8 w-8 opacity-50" />
                    {t("strengthsExplorer.leaderboardEmpty")}
                  </Card>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {sortedEntries.map((entry) => (
                      <button
                        key={entry.userId}
                        type="button"
                        onClick={() => navigate(`/admin/users/${entry.userId}/profile`)}
                        className="block rounded-lg text-start focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      >
                        <Card className="h-full p-5 transition-colors hover:border-primary/40">
                          <div className="flex min-w-0 items-center gap-4">
                            <Avatar className="h-14 w-14 shrink-0">
                              <AvatarImage src={entry.profileImageDisplayUrl || entry.profileImage || undefined} alt={entry.name} />
                              <AvatarFallback className="bg-primary text-primary-foreground">
                                {entry.name?.charAt(0).toUpperCase() || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-semibold">{entry.name}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                {entry.category && (
                                  <Badge variant="secondary" className="text-xs">
                                    {entry.category === "graduate" ? t("admin.categoryGraduate") : t("admin.categoryStudent")}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Badge
                              variant="outline"
                              className={`shrink-0 justify-center text-base ${entry.rank <= 3 ? "border-primary/40 font-bold text-primary" : ""}`}
                            >
                              #{entry.rank}
                            </Badge>
                          </div>

                          {entry.topDomains && entry.topDomains.length > 0 && (
                            <div className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3">
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Trophy className="h-3 w-3" />
                                {t("admin.topDomains")}:
                              </span>
                              {entry.topDomains.map(({ domain, count }) => {
                                const domainColor = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
                                return (
                                  <Badge
                                    key={domain}
                                    className="border text-xs"
                                    style={{ backgroundColor: `${domainColor}15`, borderColor: `${domainColor}40`, color: domainColor }}
                                  >
                                    {translateDomainName(domain, t, i18n.language)} ({count})
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </Card>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
